import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';

export interface ModuleConfig {
  id: string;
  name: string;
  enabled: boolean;
  path: string;
}

export interface AppConfig {
  modules: ModuleConfig[];
  defaultModule?: string;
  branding?: {
    title?: string;
    cssTheme?: string;
  };
  portal?: {
    name?: string;
    domain?: string;
    customerId?: string;
    colloShippingCost?: string;
    css?: string;
    portalDescription?: string;
    infoMailAddress?: string;
    phone?: string;
    [key: string]: any;
  };
}

interface ModuleConfigContextType {
  config: AppConfig | null;
  activeModule: ModuleConfig | null;
  setActiveModule: (moduleId: string) => void;
  enabledModules: ModuleConfig[];
  isLoading: boolean;
  error: string | null;
}

const ModuleConfigContext = createContext<ModuleConfigContextType | undefined>(undefined);

const defaultConfig: AppConfig = {
  modules: [
    {
      id: 'car-mats',
      name: 'products.carMats',
      enabled: true,
      path: '/car-mats'
    },
    {
      id: 'trunk-mats', 
      name: 'products.trunkMats',
      enabled: true,
      path: '/trunk-mats'
    },
    {
      id: 'other-products',
      name: 'products.otherProducts', 
      enabled: true,
      path: '/other-products'
    }
  ],
  defaultModule: 'car-mats'
};

export function ModuleConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [activeModule, setActiveModuleState] = useState<ModuleConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const enabledModules = config?.modules?.filter(module => module.enabled) || [];

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch config from worker API
        const response = await fetch(getApiUrl('/api/web/config'));
        
        console.log('Config API response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries())
        });

        if (!response.ok) {
          const responseText = await response.text();
          console.error('Config API error response:', responseText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseText = await response.text();
        console.log('Config API raw response:', responseText.substring(0, 200) + '...');
        
        let apiResponse;
        try {
          apiResponse = JSON.parse(responseText);
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError);
          console.error('Response was:', responseText);
          
          // Fall back to default config if parsing fails
          console.log('Using default config as fallback');
          const configData = defaultConfig;
          setConfig(configData);
          
          // Set active module to default or first enabled module
          const enabledMods = configData.modules.filter((m: ModuleConfig) => m.enabled);
          if (enabledMods.length > 0) {
            const defaultMod = enabledMods.find((m: ModuleConfig) => m.id === configData.defaultModule) || enabledMods[0];
            setActiveModuleState(defaultMod);
          }
          return;
        }
        
        if (apiResponse.meta?.result !== 'success') {
          throw new Error(apiResponse.meta?.message || 'Failed to load config');
        }
        
        const configData = apiResponse.data;
        
        setConfig(configData);
        
        // Set active module to default or first enabled module
        const enabledMods = configData.modules.filter((m: ModuleConfig) => m.enabled);
        if (enabledMods.length > 0) {
          const defaultMod = enabledMods.find((m: ModuleConfig) => m.id === configData.defaultModule) || enabledMods[0];
          setActiveModuleState(defaultMod);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
        console.error('Failed to load module configuration:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  const setActiveModule = (moduleId: string) => {
    const module = enabledModules.find(m => m.id === moduleId);
    if (module) {
      setActiveModuleState(module);
    }
  };

  return (
    <ModuleConfigContext.Provider value={{
      config,
      activeModule,
      setActiveModule,
      enabledModules,
      isLoading,
      error
    }}>
      {children}
    </ModuleConfigContext.Provider>
  );
}

export function useModuleConfig() {
  const context = useContext(ModuleConfigContext);
  if (context === undefined) {
    throw new Error('useModuleConfig must be used within a ModuleConfigProvider');
  }
  return context;
}